import Layout from "./Layout";

export default function CancellationPolicy() {
  return (
    <Layout>
      <div className="bg-white text-black">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-10 py-12 lg:py-16">
          {/* Page Header */}
          <div className="mb-10">
            <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-wide">
              Cancellation Policy
            </h1>

            <div className="w-16 h-1 bg-hover_dark_primary mt-4 mb-6"></div>

            <p className="text-sm text-gray-600">
              <span className="font-semibold text-black">Effective Date:</span>{" "}
              August 31, 2026
            </p>
          </div>

          {/* Introduction */}
          <div className="text-[15px] sm:text-base leading-7 text-gray-700">
            <p>
              At <strong className="text-black">Legacy Decor</strong>, orders
              are processed specifically for our customers. We therefore have a
              limited cancellation window.
            </p>
          </div>

          {/* Policy Sections */}
          <div className="mt-10 space-y-9 text-[15px] sm:text-base leading-7 text-gray-700">
            {/* 1 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                1. Cancellation Request
              </h2>

              <p>
                A customer may request cancellation of an order by contacting
                Legacy Decor as soon as possible after placing the order.
              </p>

              <p className="mt-3">Cancellation requests must include:</p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>Order number</li>
                <li>Customer name</li>
                <li>Registered mobile number/email</li>
                <li>Reason for cancellation, if requested</li>
              </ul>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                2. Cancellation Before Dispatch
              </h2>

              <p>
                If the cancellation request is received and accepted{" "}
                <strong className="text-black">
                  before the order has been dispatched
                </strong>
                , Legacy Decor may cancel the order and process the applicable
                refund.
              </p>

              <p className="mt-3">
                The refund will generally be made through the original payment
                method.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                3. Cancellation After Dispatch
              </h2>

              <p>
                Once an order has been dispatched, cancellation may not be
                possible.
              </p>

              <p className="mt-3">
                A customer may not cancel an order merely because:
              </p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>The product is no longer required</li>
                <li>The customer has changed their mind</li>
                <li>The customer does not like the product after delivery</li>
                <li>The customer has found another product</li>
                <li>The delivery is taking longer than expected</li>
              </ul>

              <p className="mt-4">
                Such circumstances do not create a right to a refund under
                Legacy Decor's standard cancellation policy.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                4. Refusal of Delivery
              </h2>

              <p>
                Customers are requested not to refuse delivery merely because
                they have changed their mind.
              </p>

              <p className="mt-3">
                Refusal of delivery does not automatically create a right to a
                refund.
              </p>

              <p className="mt-3">
                Where an order is returned to Legacy Decor following an
                unsuccessful delivery or refusal, any refund or re-shipment will
                be considered based on the circumstances and applicable law.
              </p>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                5. Cancellation by Legacy Decor
              </h2>

              <p>Legacy Decor may cancel an order where:</p>

              <ul className="list-disc pl-6 mt-3 space-y-1">
                <li>The product is unavailable</li>
                <li>
                  There is an obvious pricing or product-description error
                </li>
                <li>The delivery address is not serviceable</li>
                <li>The order appears fraudulent or suspicious</li>
                <li>The transaction cannot be verified</li>
                <li>A technical or operational error has occurred</li>
                <li>
                  Cancellation is otherwise necessary under applicable law
                </li>
              </ul>

              <p className="mt-4">
                Where Legacy Decor cancels a paid order, the applicable amount
                paid for the cancelled order will be refunded through the
                original payment method.
              </p>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                6. Refund Timeline
              </h2>

              <p>
                Once a cancellation refund is approved, Legacy Decor will
                initiate the refund within{" "}
                <strong className="text-black">5–7 business days</strong>,
                unless a different timeline is required by the applicable
                payment provider or law.
              </p>

              <p className="mt-3">
                The time taken for the refund to appear in the customer's bank
                account or card may vary depending on the payment gateway and
                financial institution.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                7. Special / Custom Orders
              </h2>

              <p>
                If Legacy Decor offers customised, personalised or made-to-order
                products in the future, cancellation terms for such products may
                differ and will be communicated at the time of purchase.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                8. Transportation Damage
              </h2>

              <p>Cancellation is separate from transportation-damage claims.</p>

              <p className="mt-3">
                If a customer receives a product damaged during transportation,
                the customer should follow the procedure specified in the{" "}
                <strong className="text-black">Refund & Damage Policy</strong>{" "}
                rather than the standard cancellation procedure.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-xl font-semibold text-black mb-3">
                9. Contact
              </h2>

              <div className="space-y-2">
                <p>
                  <strong className="text-black">Legacy Decor</strong>
                </p>

                <p>
                  Website:{" "}
                  <a
                    href="https://www.legacydecor.in/"
                    target="_blank"
                    rel="noreferrer"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    www.legacydecor.in
                  </a>
                </p>

                <p>
                  Email:{" "}
                  <a
                    href="mailto:legacydecorvintage@gmail.com"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    legacydecorvintage@gmail.com
                  </a>
                </p>

                <p>
                  Phone/WhatsApp:{" "}
                  <a
                    href="tel:+919825081632"
                    className="hover:text-hover_dark_primary hover:underline transition"
                  >
                    (+91) 9825081632
                  </a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
}
